import {
  AddonBeamLink,
  AppLayout,
  AppLayoutBody,
  AppLayoutHeader,
  AppLayoutMain,
  AppLayoutSidebar,
  Ribbon,
  Text,
} from '@bitrise/bitkit';
import React, { createContext, FunctionComponent, useContext, useState } from 'react';
import { Link, Redirect, Route, Switch, useParams } from 'react-router-dom';

import dataAtIds from '../../dataAtIds.json';
import { FeatureContext } from '../Features/useFeatures';
import useMediaQueryAppLayout from './useMediaQueryAppLayout';
import useUpdateDateParams from '../TimeRange/useUpdateDateParams';
import Attributes from '../Attributes/Attributes';
import { AuthContextFirebase } from '../Authenticate/AuthenticateFirebase';
import Crashes from '../Crashes';
import CrashesComingSoon from '../Crashes/CrashesComingSoon';
import Network from '../Network/Network';
import Performance from '../Performance';
import Settings from '../Settings/Settings';
import { URLStateContext } from '../URLState/URLState';
import AppBeam from './AppBeam';
import AppSidebar from './AppSidebar';
import BuildPerformance from '../BuildPerformance';
import Overview from '../Overview/Overview';

export const AppContext = createContext<{
  isInResponsiveView: boolean;
  isSidebarVisible: boolean;
  onHideSidebar: () => void;
  onShowSidebar: () => void;
  onToggleSidebar: () => void;
  sdkFirstSeenTime: number | null;
}>({
  isInResponsiveView: false,
  isSidebarVisible: false,
  onHideSidebar: () => undefined,
  onShowSidebar: () => undefined,
  onToggleSidebar: () => undefined,
  sdkFirstSeenTime: null,
});

const App: FunctionComponent = () => {
  const { appId } = useParams<{ appId: string }>();
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const isAppFullLayout = useMediaQueryAppLayout();
  const onHideSidebar = () => setIsSidebarVisible(false);
  const onToggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

  const { hasFeature } = useContext(FeatureContext);
  const { demo, to } = useContext(URLStateContext);
  const {
    data: { firstSeen, hasAgreedTermsOfService },
  } = useContext(AuthContextFirebase);

  useUpdateDateParams();

  return (
    <Attributes source="rollup">
      <AppLayout
        backgroundColor="gray-1"
        data-trace-at-id={dataAtIds.RootApp}
        isInResponsiveView={!isAppFullLayout}
        isSidebarVisible={isSidebarVisible}
      >
        <AppLayoutHeader>
          <AppBeam isMenuActive={isSidebarVisible} onToggleMenu={onToggleSidebar}>
            <AddonBeamLink
              Component={Link}
              data-trace-at-id={dataAtIds.AddonBeamGettingStarted}
              icon="Info"
              to={to(`/${appId}/o/getting-started`)}
            >
              Getting Started
            </AddonBeamLink>

            <AddonBeamLink
              Component={Link}
              clickable
              data-trace-at-id={dataAtIds.AddonBeamSettings}
              icon="Settings"
              to={to(`/${appId}/settings`)}
            >
              Settings
            </AddonBeamLink>
          </AppBeam>
        </AppLayoutHeader>

        <AppLayoutBody>
          <AppLayoutSidebar direction="vertical">
            <AppSidebar isAppFullLayout={isAppFullLayout} onHide={onHideSidebar} />
          </AppLayoutSidebar>

          <AppLayoutMain initial="none">
            {hasFeature('enable-ongoing-maintenance-banner') && (
              <Ribbon type="inform">
                <Text align="middle">
                  Access to Trace Beta will be closed on 1st October 2021. After that date, you will
                  no longer be able to access the Trace dashboard.
                </Text>
              </Ribbon>
            )}

            <Switch>
              {!hasAgreedTermsOfService && <Redirect to={`/${appId}/o/welcome`} />}
              {!firstSeen && !demo && !hasFeature('enable-builds-performance') && (
                <Redirect to={`/${appId}/o/getting-started`} />
              )}
              <Route component={Overview} path="/:appId/overview" />
              <Route
                component={hasFeature('enable-crashes') ? Crashes : CrashesComingSoon}
                path="/:appId/crashes"
              />
              <Route component={BuildPerformance} path="/:appId/buildPerformance" />
              <Route component={Performance} path="/:appId/appPerformance" />
              <Route component={Network} path="/:appId/network" />
              <Route component={Settings} path="/:appId/settings" />

              {/* Redirect old /performance route to /appPerformance */}
              <Redirect from="/:appId/performance" to="/:appId/appPerformance" />

              {/* Homepage */}
              {hasFeature('enable-overview') ? (
                <Redirect to={to(`/${appId}/overview`)} />
              ) : (
                <Redirect to={to(`/${appId}/appPerformance`)} />
              )}
            </Switch>
          </AppLayoutMain>
        </AppLayoutBody>
      </AppLayout>
    </Attributes>
  );
};

export default App;
